import { set, toggle } from '@shopworx/services/util/store.helper';

export default ({
  namespaced: true,
  state: {
    loading: false,
    drawer: false,
    monthlyPlans: [],
    parts: [],
    machines: [],
    colors: [],
    filterMachine: 'All',
    filterPart: 'All',
    filterMonth: 'All',
    filterColor: 'All',
    weightUnit: 'kgs',
    selectedMonthYear: null,
    actualProductionData: [],
    selectedPartDetail: null,
  },
  mutations: {
    setLoading: set('loading'),
    setDrawer: set('drawer'),
    toggleDrawer: toggle('drawer'),
    setMonthlyPlans: set('monthlyPlans'),
    setParts: set('parts'),
    setMachines: set('machines'),
    setColors: set('colors'),
    setFilterMachine: set('filterMachine'),
    setFilterPart: set('filterPart'),
    setFilterMonth: set('filterMonth'),
    setFilterColor: set('filterColor'),
    setWeightUnit: set('weightUnit'),
    setSelectedMonthYear: set('selectedMonthYear'),
    setActualProductionData: set('actualProductionData'),
    setSelectedPartDetail: set('selectedPartDetail'),
  },
  actions: {
    initElement: async ({ dispatch }) => {
      const elementExists = await dispatch(
        'element/getElement',
        'monthlyplan',
        { root: true },
      );
      if (!elementExists) {
        await dispatch('createMonthlyPlanElement');
      }
    },

    fetchParts: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        { elementName: 'part' },
        { root: true },
      );
      if (records && records.length) {
        commit('setParts', records);
      }
    },

    fetchColors: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        { elementName: 'colors' },
        { root: true },
      );
      if (records && records.length) {
        commit('setColors', records);
      }
    },

    fetchMachines: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        { elementName: 'machine' },
        { root: true },
      );
      if (records && records.length) {
        commit('setMachines', records);
      }
    },

    createMonthlyPlanElement: async ({ dispatch }) => {
      const element = {
        collectionName: 'monthlyplan',
        elementName: 'monthlyplan',
        elementDescription: 'Monthly Production Plan',
        status: 'ACTIVE',
        industryId: 1,
        categoryType: 'ASSET',
        sequence: 1,
        elementType: 'DEFAULT',
        uniqueTag: true,
        uniqueTagName: 'planid',
        uniqueTagValue: 0,
        uniqueTagStartValue: 1,
        uniqueTagValuePrefix: '200',
        uniqueTagValueSuffix: '',
        businessTimeTagsRequired: false,
        visualizer: null,
        optional: false,
        assetBased: true,
        onboardingRequired: false,
        licenseRequired: false,
      };
      const elementId = await dispatch(
        'element/createElement',
        { element },
        { root: true },
      );
      if (elementId) {
        const tags = [
          {
            tagName: 'partname',
            tagDescription: 'Part Name',
            dataType: 'string',
            required: true,
            hide: false,
            assetId: 0,
            sequence: 1,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'quantity',
            tagDescription: 'Planned Quantity',
            dataType: 'int',
            required: true,
            hide: false,
            assetId: 0,
            sequence: 2,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'netweight',
            tagDescription: 'Net Weight',
            dataType: 'string',
            required: false,
            hide: false,
            assetId: 0,
            sequence: 3,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'productionintonnage',
            tagDescription: 'Production in Tonnage',
            dataType: 'float',
            required: false,
            hide: false,
            assetId: 0,
            sequence: 4,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'monthyear',
            tagDescription: 'Month-Year',
            dataType: 'int',
            required: true,
            hide: false,
            assetId: 0,
            sequence: 5,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'machinename',
            tagDescription: 'Machine Name',
            dataType: 'string',
            required: false,
            hide: false,
            assetId: 0,
            sequence: 6,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
          {
            tagName: 'color',
            tagDescription: 'Color',
            dataType: 'string',
            required: false,
            hide: false,
            assetId: 0,
            sequence: 7,
            elementId,
            tagType: 'static',
            opcTag: '',
            plantId: '',
            enabled: true,
            status: 'ACTIVE',
          },
        ];
        const tagsCreated = await dispatch(
          'element/createElementTags',
          tags,
          { root: true },
        );
        return tagsCreated;
      }
      return false;
    },

    submitMonthlyPlan: async ({ state, dispatch }, records) => {
      try {
        const elementExists = await dispatch(
          'element/getElement',
          'monthlyplan',
          { root: true },
        );
        if (!elementExists) {
          await dispatch('createMonthlyPlanElement');
        }
        const existing = state.monthlyPlans || [];
        const toCreate = [];
        const updatePromises = [];
        records.forEach((record) => {
          // Find existing record with same part+color+month
          const match = existing.find(
            (e) => e.partname === record.partname
              && (e.color || '') === (record.color || '')
              && e.monthyear === record.monthyear,
          );
          if (match) {
            // Add quantity to existing and recalc tonnage
            const newQty = (Number(match.quantity) || 0)
              + (Number(record.quantity) || 0);
            const wt = parseFloat(record.netweight) || 0;
            const newTonnage = wt
              ? parseFloat(((newQty * wt) / 1000).toFixed(3))
              : (parseFloat(match.productionintonnage) || 0)
                + (parseFloat(record.productionintonnage) || 0);
            const id = match._id || match.id;
            const payload = {
              ...record,
              quantity: newQty,
              productionintonnage: newTonnage,
              machinename: record.machinename
                || match.machinename || '',
            };
            updatePromises.push(dispatch(
              'element/updateRecordById',
              {
                elementName: 'monthlyplan',
                id,
                payload,
              },
              { root: true },
            ));
          } else {
            toCreate.push(record);
          }
        });
        if (updatePromises.length) {
          await Promise.all(updatePromises);
        }
        if (toCreate.length) {
          const created = await dispatch(
            'element/postBulkRecords',
            {
              elementName: 'monthlyplan',
              payload: toCreate,
            },
            { root: true },
          );
          if (!created) {
            throw new Error('Failed to create records');
          }
        }
        return { success: true };
      } catch (e) {
        return {
          success: false,
          error: e.message || 'Failed to submit plan',
        };
      }
    },

    fetchMonthlyPlans: async ({ commit, state, dispatch }) => {
      commit('setLoading', true);
      let monthYear = state.selectedMonthYear;
      if (!monthYear) {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        monthYear = parseInt(`${y}${m}01`, 10);
        commit('setSelectedMonthYear', monthYear);
      }
      const records = await dispatch(
        'element/getRecords',
        {
          elementName: 'monthlyplan',
          query: `?query=monthyear==${monthYear}`,
        },
        { root: true },
      );
      if (records && records.length) {
        commit('setMonthlyPlans', records);
      } else {
        commit('setMonthlyPlans', []);
      }
      commit('setLoading', false);
    },

    fetchActualProduction: async ({
      commit, state, dispatch, rootState,
    }) => {
      try {
        const monthYear = state.selectedMonthYear;
        if (!monthYear) return;
        const s = String(monthYear);
        const year = parseInt(s.substring(0, 4), 10);
        const month = parseInt(s.substring(4, 6), 10);
        const startDate = parseInt(`${s.substring(0, 6)}01`, 10);
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = parseInt(
          `${s.substring(0, 6)}${String(lastDay).padStart(2, '0')}`,
          10,
        );
        const { activeSite } = rootState.user;
        const payload = {
          start: startDate,
          end: endDate,
          siteid: activeSite,
        };
        const reportData = await dispatch(
          'report/executeReport',
          { reportName: 'dailymachineproduction', payload },
          { root: true },
        );
        let parsed = [];
        if (reportData) {
          if (typeof reportData === 'string') {
            const obj = JSON.parse(reportData);
            parsed = Array.isArray(obj) ? obj : (obj.reportData || []);
          } else if (Array.isArray(reportData)) {
            parsed = reportData;
          }
        }
        commit('setActualProductionData', parsed);
      } catch (e) {
        commit('setActualProductionData', []);
      }
    },
  },
  getters: {
    planCount: (state) => state.monthlyPlans.length,
    actualTonnageMap: (state) => {
      const map = {};
      if (!state.actualProductionData
        || !state.actualProductionData.length) return map;
      state.actualProductionData.forEach((row) => {
        // Group by partname+color
        const colorKey = `${row.partname}||${row.color || '-'}`;
        if (!map[colorKey]) map[colorKey] = 0;
        map[colorKey] += parseFloat(row.producedweightinkgs) || 0;
        // Also group by partname only (for plans with NA color)
        const partKey = `${row.partname}||*`;
        if (!map[partKey]) map[partKey] = 0;
        map[partKey] += parseFloat(row.producedweightinkgs) || 0;
      });
      // Convert kgs to tonnage: /1000
      Object.keys(map).forEach((key) => {
        map[key] = parseFloat((map[key] / 1000).toFixed(3));
      });
      return map;
    },
    // Raw summed produced weight in kilograms (no /1000), keyed the same way
    // as actualTonnageMap. Used to show actual in Kg exactly.
    actualKgMap: (state) => {
      const map = {};
      if (!state.actualProductionData
        || !state.actualProductionData.length) return map;
      state.actualProductionData.forEach((row) => {
        const colorKey = `${row.partname}||${row.color || '-'}`;
        if (!map[colorKey]) map[colorKey] = 0;
        map[colorKey] += parseFloat(row.producedweightinkgs) || 0;
        const partKey = `${row.partname}||*`;
        if (!map[partKey]) map[partKey] = 0;
        map[partKey] += parseFloat(row.producedweightinkgs) || 0;
      });
      Object.keys(map).forEach((key) => {
        map[key] = parseFloat(map[key].toFixed(2));
      });
      return map;
    },
    filteredPlans: (state) => {
      let plans = state.monthlyPlans;
      if (state.filterMachine && state.filterMachine !== 'All') {
        plans = plans.filter((p) => p.machinename === state.filterMachine);
      }
      if (state.filterPart && state.filterPart !== 'All') {
        plans = plans.filter((p) => p.partname === state.filterPart);
      }
      if (state.filterMonth && state.filterMonth !== 'All') {
        plans = plans.filter((p) => p.monthyear === state.filterMonth);
      }
      if (state.filterColor && state.filterColor !== 'All') {
        plans = plans.filter((p) => p.color === state.filterColor);
      }
      return plans;
    },
    machineOptions: (state) => {
      const machines = state.monthlyPlans
        .map((p) => p.machinename)
        .filter((m) => m);
      return ['All', ...new Set(machines)];
    },
    partOptions: (state) => {
      const parts = state.monthlyPlans
        .map((p) => p.partname)
        .filter((p) => p);
      return ['All', ...new Set(parts)];
    },
    monthOptions: (state) => {
      const months = state.monthlyPlans
        .map((p) => p.monthyear)
        .filter((m) => m);
      return ['All', ...new Set(months)];
    },
    colorOptions: (state) => {
      const colors = state.monthlyPlans
        .map((p) => p.color)
        .filter((c) => c);
      return ['All', ...new Set(colors)];
    },
  },
});
